import FocusTrap from 'focus-trap-react';
import kebabCase from 'lodash/kebabCase';
import React, { Dispatch, SetStateAction } from 'react';
import { CallToAction } from '../../../../commonTypes/links';
import { generateKeyForElement } from '../../../../helpers/elementKeyHelper';
import { useBreakpoint } from '../../../../hooks/useBreakpoint';
import Collapsible from '../../../Collapsible';
import { Grid, GridItem } from '../../../Grid';
import GridContainer from '../../../GridContainer';
import {
  isPrimaryLinkNavItem,
  isSimplePrimaryNavItem,
  NavSectionWithCtas,
  NestedPrimaryNavItem,
  PrimaryLink,
  PrimaryNav,
  SimplePrimaryNavItem,
} from '../../types';
import { useActiveItems } from '../../useActiveItems';
import useNavKeyboardControl from '../../useNavKeyboardControl';
import withOnClick from '../../withOnClick';
import { InlineNav, NavItemText } from '../styles';
import NavLinkMenu from './Menus/NavLinkMenu';
import NestedNavMenu from './Menus/NestedNavMenu';
import SimpleNavMenu from './Menus/SimpleNavMenu';
import {
  CtaContainer,
  CtaWithMargin,
  Megamenu,
  Menu,
  NavItem,
  NavItemArrow,
} from './styles';

const getMegamenuId = (name: string): string => `${kebabCase(name)}-megamenu`;

export type PrimaryNavDesktopProps = {
  primaryNav: PrimaryNav;
  activeItemIndex: number | null;
  setActiveItemIndex: Dispatch<SetStateAction<number | null>>;
  resetNav: () => void;
  useFourColumnLayout?: boolean;
  showArrows?: boolean;
  dummyColumSectionAtEnd?: boolean;
  enableKeyboardNavigation?: boolean;
};

const PrimaryNavDesktop: React.FC<PrimaryNavDesktopProps> = ({
  primaryNav: { navItems },
  resetNav,
  useFourColumnLayout = false,
  showArrows = false,
  dummyColumSectionAtEnd = false,
  enableKeyboardNavigation,
  ...activeItemProps
}) => {
  // We proxy the active item prop coming in with our own state so that we can control and
  // monitor the current and previous active items. This allows us to create smooth animations
  // on closing the nav; we can continue to show the last-selected nav items until the nav has
  // completely collapsed.
  const [
    {
      activeItemIndex,
      previousActiveItemIndex,
      activeNestedItemIndex,
      previousNestedItemIndex,
    },
    onClickNavItem,
    onClickNestedNavItem,
  ] = useActiveItems(activeItemProps.activeItemIndex, activeItemProps.setActiveItemIndex);
  const navListRef = useNavKeyboardControl<HTMLDivElement>();
  const breakpoint = useBreakpoint() ?? 'mobile';

  const getActiveNestedNavItemCtas = (
    navSection: NavSectionWithCtas[]
  ): CallToAction[] | null => {
    return activeNestedItemIndex !== null ? navSection[activeNestedItemIndex].ctas : null;
  };

  const navItemIndexToShow = activeItemIndex ?? previousActiveItemIndex;
  const navItemToShow = navItemIndexToShow !== null && navItems[navItemIndexToShow];
  const getCtasToDisplay = (): CallToAction[] | null => {
    if (navItemToShow) {
      if (isPrimaryLinkNavItem(navItemToShow)) {
        return null;
      }
      if (isSimplePrimaryNavItem(navItemToShow)) {
        return navItemToShow.ctas;
      }
      return getActiveNestedNavItemCtas(navItemToShow.primarySection.subsections);
    }
    return null;
  };

  const ctasToDisplay = getCtasToDisplay();

  const onClick = (
    event: React.MouseEvent,
    navItem: SimplePrimaryNavItem | NestedPrimaryNavItem,
    index: number
  ): void => {
    if (navItem.onExpand && activeItemIndex !== index) {
      navItem.onExpand(event);
    }
    if (navItem.onCollapse && activeItemIndex === index) {
      navItem.onCollapse(event);
    }
    onClickNavItem(index)();
  };

  return (
    <FocusTrap
      active={
        activeItemIndex !== null &&
        (breakpoint === 'desktop' || breakpoint === 'tabletLandscape')
      }
      focusTrapOptions={{ clickOutsideDeactivates: true }}>
      <InlineNav
        data-testid="primary-nav"
        ref={enableKeyboardNavigation ? navListRef : undefined}>
        {navItems &&
          navItems.map((navItem, index) => (
            <div key={navItem.name}>
              {isPrimaryLinkNavItem(navItem) ? (
                <NavLinkMenu navItem={navItem as PrimaryLink} />
              ) : (
                <>
                  <NavItem
                    type="button"
                    onClick={(event) => onClick(event, navItem, index)}
                    isActive={activeItemIndex === index}
                    aria-expanded={activeItemIndex === index}
                    aria-haspopup
                    aria-controls={getMegamenuId(navItem.name)}>
                    <NavItemText>{navItem.name}</NavItemText>
                    {showArrows && <NavItemArrow name="arrow-down" size="x-small" />}
                  </NavItem>
                  {activeItemIndex === index && (
                    <Megamenu>
                      <Collapsible isOpened={activeItemIndex !== null}>
                        {navItemToShow && (
                          <div id={getMegamenuId(navItemToShow.name)}>
                            <Menu>
                              <GridContainer>
                                <Grid alignLeft={useFourColumnLayout}>
                                  {isSimplePrimaryNavItem(navItemToShow) ? (
                                    <SimpleNavMenu
                                      navItem={navItemToShow}
                                      resetNav={resetNav}
                                      useFourColumnLayout={useFourColumnLayout}
                                      dummyColumSectionAtEnd={dummyColumSectionAtEnd}
                                    />
                                  ) : (
                                    <NestedNavMenu
                                      navItem={navItemToShow as NestedPrimaryNavItem}
                                      activeNestedItemIndex={
                                        activeNestedItemIndex ?? previousNestedItemIndex
                                      }
                                      setActiveNestedItemIndex={onClickNestedNavItem}
                                      resetNav={resetNav}
                                    />
                                  )}
                                </Grid>
                              </GridContainer>
                            </Menu>
                            <CtaContainer>
                              <GridContainer>
                                <Grid>
                                  <GridItem desktop={10} tabletLandscape={10}>
                                    {ctasToDisplay &&
                                      ctasToDisplay.map((cta) => (
                                        <CtaWithMargin
                                          key={generateKeyForElement(cta)}
                                          cta={withOnClick(cta, resetNav)}
                                        />
                                      ))}
                                  </GridItem>
                                </Grid>
                              </GridContainer>
                            </CtaContainer>
                          </div>
                        )}
                      </Collapsible>
                    </Megamenu>
                  )}
                </>
              )}
            </div>
          ))}
      </InlineNav>
    </FocusTrap>
  );
};

export default PrimaryNavDesktop;
